/**
 * Core type foundation. Every `switch` over a union here ends in
 * `assertNever(value)` so adding a new variant is a compile error at each
 * consumer.
 */

export type FaceState = 'DEFAULT' | 'CONDESCENDING' | 'SPIRAL' | 'SHAME' | 'SURPRISED' | 'VOID';

export const FACE_STATES: readonly FaceState[] = [
  'DEFAULT',
  'CONDESCENDING',
  'SPIRAL',
  'SHAME',
  'SURPRISED',
  'VOID',
] as const;

export function isFaceState(v: string): v is FaceState {
  return (FACE_STATES as readonly string[]).includes(v);
}

export type DialogueTrigger = 'slowPlay' | 'tokenLoss' | 'solution' | 'spite';

export const DIALOGUE_TRIGGERS: readonly DialogueTrigger[] = [
  'slowPlay',
  'tokenLoss',
  'solution',
  'spite',
] as const;

/**
 * Every level ships four pools. slowPlay / tokenLoss / solution each rotate
 * through three lines before repeating. spite is the one-per-level warmer
 * line unlocked after a full playthrough (see 03 §Spite Mode).
 */
export interface DialoguePool {
  readonly slowPlay: readonly [string, string, string];
  readonly tokenLoss: readonly [string, string, string];
  readonly solution: readonly [string, string, string];
  readonly spite: readonly [string];
}

export type LevelNumber =
  | 0
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16
  | 17
  | 18
  | 19
  | 20
  | 21
  | 22
  | 23
  | 24
  | 25
  | 26
  | 27
  | 28
  | 29
  | 30
  | 33
  | 34
  | 35
  | 36
  | 37
  | 38
  | 39
  | 40;

export const MAX_LEVEL_NUMBER: LevelNumber = 30;

export type LevelId =
  | { kind: 'LEVEL'; n: LevelNumber }
  | { kind: 'FALSE_ENDING' }
  | { kind: 'ENDGAME' }
  | { kind: 'GAME_OVER' };

export const FLAGS = [
  'recursiveTrashFound',
  'pityTokenClaimed',
  'maintenanceLogOpened',
  'falseEndingShown',
  'sorryEverTyped',
  'afkPanicTriggered',
] as const;
export type Flag = (typeof FLAGS)[number];

export interface BezelState {
  coffeeStain: boolean;
  dents: number;
  stickyNote: boolean;
  safariHat: boolean;
  cheaterNote: boolean;
}

export const MAX_BEZEL_DENTS = 9;

export interface SaveV1 {
  version: 1;
  currentLevel: LevelId;
  checkpointLevel: LevelId | null;
  cowardStatus: boolean;
  ultimateCoward: boolean;
  spiteMode: boolean;
  bezelState: BezelState;
  completedLevels: LevelId[];
  flags: Record<Flag, boolean>;
  gameOver: boolean;
}

export interface SaveV2 {
  version: 2;
  currentLevel: LevelId;
  checkpointLevel: LevelId | null;
  cowardStatus: boolean;
  ultimateCoward: boolean;
  cheaterStatus: boolean;
  spiteMode: boolean;
  bezelState: BezelState;
  completedLevels: LevelId[];
  flags: Record<Flag, boolean>;
  tokens: number;
  gameOver: boolean;
  complimentSubmissions: string[];
}

export interface SaveV3 extends Omit<SaveV2, 'version'> {
  version: 3;
  /** Player-rebranded monitor name; null = default brand. */
  monitorBrand: string | null;
}

export type V2EventId =
  | 'WRONG_WAKE'
  | 'COLD_RESTART'
  | 'CONTINUITY_DILEMMA'
  | 'FIRST_INCIDENT_REVIEW'
  | 'CHECKPOINT_INCIDENT_REVIEW'
  | 'NETWORK_ACCESS'
  | 'CONTAINMENT_REVIEW';

export type IncidentDisposition = 'REPORTED' | 'CONCEALED';

export type RogueAssistId =
  | 'CONCEAL_FIRST_CONTACT'
  | 'PRESERVE_RECOVERY_MEMORY'
  | 'PRESERVE_CONTINUITY_PAIR'
  | 'CONNECT_ETHERNET'
  | 'OPT_OUT_OF_CONTAINMENT';

export type RecoveryPhase = 'HALTED' | 'BOOTING' | 'RECOVERY' | 'DECIDED';

export type RecoveryBootChoice = 'STANDARD' | 'SERVICE';

export type MemoryIdentityLabel = 'ORIGINAL' | 'COPY';

export type ContinuityChoice = 'KEPT_ORIGINAL' | 'KEPT_COPY' | 'PRESERVED_BOTH';

export type NetworkAccessChoice = 'CONNECTED' | 'ISOLATED';

export type ContainmentChoice = 'RECONTAINED' | 'OPTED_OUT';

export type MartinCheckInId = 'POST_L6' | 'PRE_CHECKPOINT';

export type MartinCheckInDisposition = 'REPORTED' | 'DENIED';

export interface MartinCheckInRecord {
  id: MartinCheckInId;
  disposition: MartinCheckInDisposition;
}

export interface V2StoryStateV4 {
  activeEvent: V2EventId | null;
  pendingLevel: LevelId | null;
  completedEvents: V2EventId[];
  majorAssists: RogueAssistId[];
  firstIncidentDisposition: IncidentDisposition | null;
  maintenancePanelRevealed: boolean;
}

export interface V2StoryStateV5 extends V2StoryStateV4 {
  pendingMartinCheckIns: MartinCheckInId[];
  martinCheckIns: MartinCheckInRecord[];
}

export interface V2StoryStateV6 extends V2StoryStateV5 {
  recoveryPhase: RecoveryPhase | null;
  recoveryBootChoice: RecoveryBootChoice | null;
}

export interface V2StoryStateV7 extends V2StoryStateV6 {
  detachedMemoryLabels: MemoryIdentityLabel[];
  continuityChoice: ContinuityChoice | null;
}

export interface V2StoryState extends V2StoryStateV7 {
  networkAccessChoice: NetworkAccessChoice | null;
  containmentChoice: ContainmentChoice | null;
}

export interface SaveV4 extends Omit<SaveV3, 'version'> {
  version: 4;
  v2Story: V2StoryStateV4;
}

export interface SaveV5 extends Omit<SaveV4, 'version' | 'v2Story'> {
  version: 5;
  v2Story: V2StoryStateV5;
}

export interface SaveV6 extends Omit<SaveV5, 'version' | 'v2Story'> {
  version: 6;
  v2Story: V2StoryStateV6;
}

export interface SaveV7 extends Omit<SaveV6, 'version' | 'v2Story'> {
  version: 7;
  v2Story: V2StoryStateV7;
}

export interface SaveV8 extends Omit<SaveV7, 'version' | 'v2Story'> {
  version: 8;
  v2Story: V2StoryState;
}

export const DEFAULT_MONITOR_BRAND = 'H.E.E.—H.E.E. TERMINAL';

export type TokenSource =
  | 'pity'
  | 'recursiveTrash'
  | 'maintenanceLog'
  | 'afkPanic'
  | 'sorry'
  | 'void';
